import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  ChevronDown, Sparkles, LogOut, Shield, ShieldCheck, Home, Slack, Camera, BarChart3, Rocket
} from 'lucide-react';
import NotificationsBell from './NotificationsBell';
import FollowUpsBadge from './FollowUpsBadge';
import GradientAvatar from './GradientAvatar';
import ConnectSlackDialog from './ConnectSlackDialog';
import { uploadAvatarFile } from '../lib/avatarUpload';

const Header = () => {
  const [showTools, setShowTools] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSlack, setShowSlack] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout, isAdmin, refreshUser } = useAuth();
  const isLeadsViewer = user?.role === 'leads_viewer';
  const isBrokerSupport = user?.role === 'broker_support';
  const isRestricted = isLeadsViewer || isBrokerSupport;

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadAvatarFile(file);
      await refreshUser();
      setUploading(false);
      setShowUserMenu(false);
    } catch (err) {
      alert('Could not update photo. Please try a smaller image.');
      setUploading(false);
    }
  };

  return (
    <header
      className="bg-white/80 dark:bg-slate-950/95 backdrop-blur-xl border-b border-slate-200/70 dark:border-slate-800/80 sticky top-0 z-40 shadow-sm"
      data-testid="app-header"
    >
      <div className="w-full pl-5 pr-3 sm:pl-10 sm:pr-6 h-14 flex items-center">
        <div className="flex items-center justify-between gap-2 w-full">
          {/* Wordmark */}
          <Link
            to={isBrokerSupport ? '/onboarding' : isLeadsViewer ? '/leads-viewer-hub' : '/'}
            className="relative flex items-center group focus:outline-none focus:ring-2 focus:ring-brand rounded-lg min-w-0"
            aria-label={isRestricted ? 'Client Onboarding' : 'Back to Home'}
            data-testid="header-logo-link"
          >
            <img
              src="/derwent-ai-logo.png"
              alt="Derwent AI"
              className="h-[22px] sm:h-6 w-auto object-contain select-none transition-transform duration-200 group-hover:scale-[1.03]"
              draggable={false}
            />
          </Link>

          <div className="flex items-center gap-0.5 sm:gap-1.5 flex-shrink-0">
            {/* Home / Notifications / Follow-ups — hidden for restricted roles */}
            {!isRestricted && (
              <>
                <Button
                  onClick={() => navigate('/')}
                  size="sm"
                  variant="outline"
                  className="rounded-full h-9 px-2.5 sm:px-3.5 border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200 font-semibold"
                  data-testid="header-home-btn"
                  aria-label="Go to home page"
                >
                  <Home className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline text-xs font-bold">Home</span>
                </Button>

                <NotificationsBell />
                <FollowUpsBadge />
              </>
            )}

            {/* Home + Game Plan buttons for broker_support assistant account — always visible */}
            {isBrokerSupport && (
              <>
                <Button
                  onClick={() => navigate('/')}
                  size="sm"
                  variant="outline"
                  className="rounded-full h-9 px-2.5 sm:px-3.5 border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200 font-semibold"
                  data-testid="header-home-btn"
                  aria-label="Go to home page"
                >
                  <Home className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline text-xs font-bold">Home</span>
                </Button>
                <Button
                  onClick={() => navigate('/game-plan')}
                  size="sm"
                  variant="outline"
                  className="rounded-full h-9 px-2.5 sm:px-3.5 border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200 font-semibold"
                  data-testid="header-gameplan-btn"
                  aria-label="Open Game Plan Tracker"
                >
                  <BarChart3 className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline text-xs font-bold">Game Plan</span>
                </Button>
              </>
            )}

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-2.5 py-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800/70 transition-colors"
                data-testid="user-menu-btn"
                aria-label="User menu"
              >
                <GradientAvatar
                  name={user?.name}
                  role={user?.role}
                  keyId={user?.id || user?.email || user?.name}
                  size="sm"
                  testId="header-user-avatar"
                  side="bottom"
                  imageUrl={user?.avatar_url}
                />
                <div className="hidden md:block text-left">
                  <p className="text-sm font-bold text-slate-950 dark:text-white leading-tight">{user?.name || 'User'}</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 capitalize leading-tight">{user?.role || 'broker'}</p>
                </div>
                <ChevronDown className={`hidden md:block w-4 h-4 text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-60 bg-white dark:bg-slate-900 rounded-2xl shadow-soft border border-slate-200 dark:border-slate-700 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-pastel-lavender/30 dark:bg-slate-800/60">
                    <p className="font-bold text-slate-950 dark:text-white">{user?.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                  </div>

                  {isAdmin() && !isRestricted && (
                    <>
                      <button
                        onClick={() => { navigate('/admin'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-pastel-butter/30 dark:hover:bg-slate-800 transition-colors"
                        data-testid="admin-dashboard-link"
                      >
                        <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Admin Dashboard</span>
                      </button>
                      <button
                        onClick={() => { navigate('/security'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-800 transition-colors"
                        data-testid="security-centre-link"
                      >
                        <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Security Centre</span>
                      </button>
                    </>
                  )}

                  {!isBrokerSupport && (
                  <button
                    onClick={() => { navigate('/setup'); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-sky-50 dark:hover:bg-slate-800 transition-colors"
                    data-testid="broker-setup-link"
                  >
                    <Rocket className="w-4 h-4 text-sky-600 dark:text-sky-300" />
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Workspace setup</span>
                  </button>
                  )}
                  {!isBrokerSupport && (
                  <button
                    onClick={() => { setShowSlack(true); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-pastel-lavender/40 dark:hover:bg-slate-800 transition-colors"
                    data-testid="connect-slack-link"
                  >
                    <Slack className="w-4 h-4 text-[#4A154B] dark:text-fuchsia-300" />
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Connect Slack</span>
                  </button>
                  )}

                  <button
                    onClick={() => fileRef.current?.click()}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    data-testid="header-change-photo"
                  >
                    <Camera className="w-4 h-4 text-slate-500 dark:text-slate-300" />
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{uploading ? 'Uploading…' : 'Change photo'}</span>
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="header-avatar-file-input" />

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors text-rose-600 dark:text-rose-400"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm font-semibold">Sign Out</span>
                  </button>
                </div>
              )}
            </div>

            {/* Tools Menu — hidden for restricted roles */}
            {!isRestricted && (
            <div className="relative">
              <Button
                onClick={() => setShowTools(!showTools)}
                size="sm"
                className="bg-brand hover:opacity-90 text-white border-0 rounded-full shadow-sm transition-opacity px-2.5 sm:px-3.5 h-9"
                data-testid="header-tools-btn"
                aria-label="Open tools menu"
              >
                <Sparkles className="w-4 h-4 sm:hidden" />
                <span className="hidden sm:inline font-bold">Tools</span>
                <ChevronDown className={`hidden sm:inline-block ml-1 h-4 w-4 transition-transform ${showTools ? 'rotate-180' : ''}`} />
              </Button>

              {showTools && (
                <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-white dark:bg-slate-900 rounded-2xl shadow-soft border border-slate-200 dark:border-slate-700 z-50 max-h-[80vh] overflow-y-auto">
                  <div
                    className="px-5 py-4 hover:bg-pastel-lavender/40 dark:hover:bg-slate-800/70 cursor-pointer transition-all bg-pastel-lavender/60 dark:bg-cyan-500/10 dark:bg-gradient-to-r dark:from-cyan-500/10 dark:via-slate-900 dark:to-slate-900 rounded-t-2xl"
                    onClick={() => { navigate('/myweek'); setShowTools(false); }}
                    data-testid="header-derwent-link"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="h-4 w-4 text-slate-900 dark:text-cyan-400" />
                      <span className="font-extrabold text-slate-950 dark:text-cyan-300 text-base">MyWeek</span>
                    </div>
                    <div className="text-xs text-slate-700 dark:text-cyan-400/80 font-medium ml-6">Your personal AI operating system — talk to Hey Andi</div>
                  </div>

                  {[
                    { path: '/derwentai-reports', title: 'DERWENTAI Reports', desc: 'Automated Slack performance updates — 3×/day AI CEO summary' },
                    ...(isAdmin() ? [{ path: '/security', title: 'Security Centre', desc: 'MFA, audit trail, alerts, encrypted backups, policies' }] : []),
                    { path: '/game-plan', title: 'Game Plan Tracker', desc: 'Submissions & approvals pipeline' },
                    { path: '/lender-packs', title: 'My Derwent Hub', desc: 'Central hub · Pre-Lodgement, Post-Lodgement, My Dashboard · autosave · dual PDFs' },
                    { path: '/work', title: 'Work', desc: 'Unified To-Do list — todos, lead follow-ups & Hey Andi reminders in one feed' },
                    { path: '/derwent-hub', title: 'My Derwent Hub', desc: 'Long-term client portal · loan journey · docs · goals' },
                    { path: '/settled-loans', title: 'Settled Loans', desc: 'Post-settlement loan book · retention · trail' },
                    { path: '/pre-built-scripts', title: 'Pre-Built Scripts', desc: '60 ready-to-use SMS, email & call scripts' },
                    { path: '/marketing', title: 'Marketing Command Centre', desc: 'Ideas, requests, hooks & content workflow' },
                    { path: '/read-my-income', title: 'Read My Income', desc: 'AI payslip analysis & lender match' },
                    { path: '/calls-to-text', title: 'Calls to Text', desc: 'Whisper transcription' },
                    { path: '/accelerate-calculator', title: 'Accelerate Consolidation', desc: 'Debt consolidation & offset strategies' },
                    { path: '/bdm-contacts', title: 'BDM Contacts', desc: 'Find your Business Development Manager' },
                    { path: '/blueprint-breakdown', title: 'Blueprint Break Down', desc: 'Activity & commission calculator' },
                    { path: '/breakdown-report', title: 'Break Down Report', desc: 'Funds required vs available' },
                    { path: '/client-management', title: 'Client Management', desc: 'Manage client portal accounts' },
                    { path: '/client-portal-templates', title: 'Portal Templates', desc: 'Email / SMS / portal-message templates' },
                    { path: '/client-portal-reports', title: 'Portal Reports', desc: 'Conversion funnel, settlements, reviews' },
                    { path: '/compliance', title: 'Compliance', desc: 'Regulatory guides & templates' },
                    { path: '/construction-cost-calculator', title: 'Construction Cost Calculator', desc: 'Build cost estimator' },
                    { path: '/discharges', title: 'Discharge Forms', desc: 'Lender discharge authority forms' },
                    { path: '/hot-off-the-press', title: 'Hot off the Press', desc: 'Latest industry news' },
                    { path: '/home-loan-calculator', title: 'Home Loan Calculator', desc: 'Monthly repayment calculator' },
                    { path: '/investor-property-report', title: 'Investor Property Report', desc: 'Investment analysis tool' },
                    { path: '/lmi-calculator', title: 'LMI Calculator', desc: 'Lenders mortgage insurance' },
                    { path: '/lmi-vs-wait-calculator', title: 'LMI vs Wait', desc: 'Save now or wait comparison' },
                    { path: '/lvr-calculator', title: 'LVR Calculator', desc: 'Loan to value ratio' },
                    { path: '/notes-ai', title: 'Submission Notes AI', desc: 'Generate broker notes with AI' }
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      className="px-5 py-3 hover:bg-pastel-lavender/30 dark:hover:bg-slate-800/70 cursor-pointer transition-colors border-b border-slate-100 dark:border-slate-800 last:border-b-0"
                      onClick={() => { navigate(item.path); setShowTools(false); }}
                    >
                      <div className="font-bold text-slate-900 dark:text-slate-100 text-sm">{item.title}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{item.desc}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </div>
      <ConnectSlackDialog open={showSlack} onClose={() => setShowSlack(false)} />
    </header>
  );
};

export default Header;
